import { html, css, LitElement } from 'lit';
import { property, state } from 'lit/decorators.js';
import './seb-textfield.js';
import './seb-autocomplete.js';
import './seb-button.js';
import './seb-checkbox.js';
import countries from './utils/countries.js';
import estados from './utils/estados.js';
import citiesService from './services/citiesService.js';

export class SebContactForm extends LitElement {
  static styles = css`
    :host {
      display: block;
      padding: 25px;
    }

    :host form fieldset {
      border: none;
      padding: 0;
      margin: 0 0 10px;
      width: 100%;
    }

    :host form fieldset div.row {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      width: 100%;
      gap: 10px;
      margin-bottom: 20px;
    }

    :host form div.form-group {
      width: 100%;
      max-width: 50%;
    }

    :host form .btn-submit {
      display: flex;
      justify-content: flex-end;
      width: auto;
    }

    :host p {
      margin-top: 0;
      margin-bottom: 0;
    }
  `;

  @property({ type: String }) action = '';

  @property({ type: String }) btnSubmitTextColor = 'white';

  @property({ type: String }) btnSubmitText = 'Submit';

  @property({ type: String }) loadingText = 'Loading...';

  @property({ type: String }) noResultsText = 'No results found';

  @property({ type: Object }) fieldsLabels: Record<string, string> = {};

  @state() stateUF = '';

  @state() cities: { text: string }[] = [];

  @state() loadingCities = false;

  @state() formValues: Record<string, string> = {};

  __handleStateChange(e: Event) {
    const input = e.target as HTMLInputElement;
    const estado = estados.find(s => s.nome === input.value);
    if (estado) {
      this.stateUF = estado.sigla;
      this.loadingCities = true;
      this.formValues = {
        ...this.formValues,
        state_uf: this.stateUF,
      };
      citiesService(this.stateUF).then((data: { nome: string }[]) => {
        this.cities = data.map(d => ({ text: d.nome }));
        this.loadingCities = false;
      });
    }

    this.__handleInputChange(e);
  }

  __submit() {
    this.formValues = {
      ...this.formValues,
      country_abbr:
        countries.find(c => c.nome_pais === this.formValues.country)?.sigla ||
        '',
      zip: this.formValues?.zip?.replace(/\D/g, '') || '',
      phone: this.formValues?.phone?.replace(/\D/g, '') || '',
    };

    if (this.action) {
      const formData = new FormData();
      Object.entries(this.formValues).forEach(([key, value]) => {
        formData.append(key, value);
      });
      fetch(this.action, {
        method: 'POST',
        body: formData,
      });
    } else {
      this.dispatchEvent(
        new CustomEvent('submit', {
          detail: {
            values: this.formValues,
          },
        })
      );
    }
  }

  __handleInputChange(e: Event) {
    const input = e.target as HTMLInputElement;
    this.formValues = {
      ...this.formValues,
      [input.name]: input.value,
    };
  }

  render() {
    const fieldsLabels = this.fieldsLabels || {
      name: 'Name',
      email: 'E-mail',
      phone: 'Phone',
      zip: 'Zip code',
      country: 'Country',
      state: 'State',
      city: 'City',
    };

    let states = '[]';
    let cities = '[]';

    if (this.formValues?.country === 'Brasil') {
      states = JSON.stringify(
        estados.map(e => ({ text: e.nome, value: e.sigla }))
      );

      if (this.stateUF) {
        cities = JSON.stringify(this.cities);
      }
    }

    return html`
      <form action=${this.action}>
        <fieldset>
          <div class="row">
            <div class="form-group">
              <seb-textfield
                class="field"
                label=${fieldsLabels.name}
                name="name"
                @input=${this.__handleInputChange}
              />
            </div>

            <div class="form-group">
              <seb-textfield
                class="field"
                label=${fieldsLabels.email}
                name="email"
                type="email"
                @input=${this.__handleInputChange}
              />
            </div>
          </div>

          <div class="row">
            <div class="form-group">
              <seb-textfield
                class="field"
                label=${fieldsLabels.phone}
                name="phone"
                @input=${this.__handleInputChange}
              />
            </div>

            <div class="form-group">
              <seb-textfield
                class="field"
                label=${fieldsLabels.zip}
                name="zip"
                type="text"
                @input=${this.__handleInputChange}
              />
            </div>
          </div>

          <div class="row">
            <div class="form-group">
              <seb-autocomplete
                class="field"
                label=${fieldsLabels.country}
                name="country"
                options=${JSON.stringify(
                  countries.map(c => ({ text: c.nome_pais }))
                )}
                @input=${this.__handleInputChange}
                noResultsText=${this.loadingCities
                  ? this.loadingText
                  : this.noResultsText}
              />
            </div>

            <div class="form-group">
              <seb-autocomplete
                class="field"
                label=${fieldsLabels.state}
                name="state"
                options=${states}
                @input=${this.__handleStateChange}
                noResultsText=${this.loadingCities
                  ? this.loadingText
                  : this.noResultsText}
              />
            </div>
          </div>

          <div class="row">
            <div class="form-group">
              <seb-autocomplete
                class="field"
                label=${fieldsLabels.city}
                name="city"
                options=${cities}
                @input=${this.__handleInputChange}
                noResultsText=${this.loadingCities
                  ? this.loadingText
                  : this.noResultsText}
              />
            </div>

            <div class="form-group">
              <p>Áreas de interesse</p>

              <div class="row">
                <seb-checkbox
                  name="graduation-check"
                  @input=${this.__handleInputChange}
                >
                  Graduação
                </seb-checkbox>

                <seb-checkbox
                  name="post-graduation-check"
                  @input=${this.__handleInputChange}
                >
                  Pós-Graduação
                </seb-checkbox>

                <seb-checkbox
                  name="corporative-education-check"
                  @input=${this.__handleInputChange}
                >
                  Educação Corporativa
                </seb-checkbox>
              </div>
            </div>
          </div>
        </fieldset>

        <seb-button
          class="btn-submit"
          type="button"
          style=${`--seb-text-color: ${this.btnSubmitTextColor}; --seb-secondary-color: #000;`}
          .click=${() => this.__submit()}
        >
          ${this.btnSubmitText}
        </seb-button>
      </form>
    `;
  }
}
